import {
	useEffect,
	useState,
	forwardRef,
	useImperativeHandle,
	useMemo,
	useCallback
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColDef } from 'ag-grid-community';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';
import { toast } from 'sonner';

import { ListPageLayout } from '@/components/layout/list-page-layout';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { VoucherDetails, VoucherDetailsHeader } from './voucher-details';
import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';

import { cloneVoucher } from './cloneVoucher';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showDeleteColumn } from '@/lib/featureFlags';
import { VOUCHER_DELETE_MUTATION, VOUCHER_QUERY, VOUCHERS_LIST_QUERY } from './vouchers.gql';
import {
	selectVoucherAfterSave,
	type VoucherListRecord
} from './voucher-list-selection';

interface VoucherListItem {
	voucherId: string;
	code: string;
	name?: string | null;
	isAdHoc: boolean;
	isCustomerSpecific: boolean;
	discountValue: number;
	maxDiscountAmount?: number | null;
	freeTimeMinutes?: number | null;
	usageLimit?: number | null;
	currentUsageCount: number;
	isActive: boolean;
	validUntilDate: string;
	voucherDiscountTypeEnum?: string | null;
	voucherDiscountType?: {
		voucherDiscountTypeId: number;
		name?: string | null;
	} | null;
	campaign?: { campaignId: string; name?: string | null } | null;
}

const formatDate = (value: string | null | undefined) => {
	if (!value) {
		return '—';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleDateString();
};

export interface VouchersListRef {
	selectVoucherAfterSave: (voucherId: string) => Promise<void>;
}

export const VouchersList = forwardRef<VouchersListRef>((_, ref) => {
	const { t } = useTranslation('vouchers');
	const { showDialog } = useConfirmationDialog();
	const [localVouchers, setLocalVouchers] = useState<VoucherListRecord[]>([]);
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);
	const [activeDetailsTab, setActiveDetailsTab] = useState('details');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();

	const [deleteVoucher, { loading: deleteLoading }] = useMutation(
		VOUCHER_DELETE_MUTATION
	);

	const { data, loading: isLoading, refetch, fetchMore } = useQuery(
		VOUCHERS_LIST_QUERY,
		{
			variables: { first: itemsToFetch },
			fetchPolicy: 'cache-and-network'
		}
	);
	const serverVouchers = data?.vouchers?.nodes ?? [];

	// Pagination state
	const pageInfo = data?.vouchers?.pageInfo;
	const hasNextPage = pageInfo?.hasNextPage ?? false;
	const endCursor = pageInfo?.endCursor;
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;
					const newNodes = fetchMoreResult.vouchers?.nodes || [];
					const prevNodes = prev.vouchers?.nodes || [];
					setItemsToFetch(prevNodes.length + newNodes.length);
					setLocalVouchers([]);
					return {
						...prev,
						vouchers: {
							...fetchMoreResult.vouchers,
							nodes: [...prevNodes, ...newNodes]
						}
					};
				}
			});
		} finally {
			setIsLoadingMore(false);
		}
	}, [hasNextPage, isLoadingMore, endCursor, fetchMore]);

	const goToCreate = useCallback(() => {
		navigate('/vouchers/create');
	}, [navigate]);

	const goToEdit = useCallback(
		(id: string) => {
			navigate(`/vouchers/edit/${id}`);
		},
		[navigate]
	);

	const refresh = useCallback(() => {
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		setLocalVouchers([]);
		refetch({ first: DEFAULT_PAGE_SIZE });
	}, [refetch]);

	// Use local vouchers data if available, otherwise fall back to server data
	const vouchers = useMemo(() => {
		// Always prefer server data - local state is only for optimistic updates
		if (localVouchers.length === 0 || serverVouchers.length === 0) {
			return serverVouchers;
		}
		return localVouchers;
	}, [serverVouchers, localVouchers]);

	// Expose method to select voucher after save via ref
	useImperativeHandle(
		ref,
		() => ({
			selectVoucherAfterSave: async (voucherId: string) => {
				await selectVoucherAfterSave({
					voucherId,
					client: apolloClient,
					currentVouchers: vouchers,
					onDataUpdate: setLocalVouchers,
					onVoucherSelect: setSelectedVoucher
				});
			}
		}),
		[apolloClient, vouchers]
	);

	// Handle voucher selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectVoucherId?: string } | null;
		if (state?.selectVoucherId && vouchers.length > 0 && !isLoading) {
			const voucherIdToSelect = state.selectVoucherId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectVoucherAfterSave({
						voucherId: voucherIdToSelect,
						client: apolloClient,
						currentVouchers: vouchers,
						onDataUpdate: setLocalVouchers,
						onVoucherSelect: setSelectedVoucher
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select voucher after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		vouchers.length,
		isLoading,
		apolloClient,
		vouchers,
		navigate,
		location.pathname
	]);

	const [selectedVoucher, setSelectedVoucher] =
		useState<VoucherListItem | null>(null);
	const [pendingSelectionId, setPendingSelectionId] = useState<string | null>(
		null
	);

	useEffect(() => {
		if (!selectedVoucher && vouchers.length > 0) {
			setSelectedVoucher(vouchers[0]);
			return;
		}

		if (selectedVoucher) {
			const updated = vouchers.find(
				(item: VoucherListItem) => item.voucherId === selectedVoucher.voucherId
			);

			if (!updated) {
				setSelectedVoucher(null);
				return;
			}

			if (updated !== selectedVoucher) {
				setSelectedVoucher(updated);
			}
		}
	}, [selectedVoucher, vouchers]);

	// Handle pending selection after data refresh
	useEffect(() => {
		if (pendingSelectionId && vouchers) {
			const voucherToSelect = vouchers.find(
				(v: VoucherListItem) => v.voucherId === pendingSelectionId
			);
			if (voucherToSelect) {
				setSelectedVoucher(voucherToSelect);
				setPendingSelectionId(null); // Clear pending selection
			}
		}
	}, [vouchers, pendingSelectionId]);

	// Helper function to find the next voucher to select after deletion
	const findNextVoucherToSelect = (
		deletedVoucherId: string,
		voucherList: VoucherListItem[]
	): string | null => {
		const currentIndex = voucherList.findIndex(
			v => v.voucherId === deletedVoucherId
		);

		if (voucherList.length <= 1) {
			return null;
		}

		// Try to select the next voucher (same index after deletion)
		if (currentIndex < voucherList.length - 1) {
			return voucherList[currentIndex + 1].voucherId;
		}

		// If deleting the last voucher, select the previous one
		if (currentIndex > 0) {
			return voucherList[currentIndex - 1].voucherId;
		}

		return null;
	};

	const handleDeleteVoucher = async (voucher: VoucherListItem) => {
		const voucherName = voucher.name?.trim() || voucher.code;

		const confirmed = await showDialog(
			t('vouchers:messages.deleteTitle'),
			t('vouchers:messages.deleteConfirm', { name: voucherName })
		);

		if (confirmed) {
			// Determine if we need to find the next voucher to select
			const wasSelectedVoucher =
				selectedVoucher?.voucherId === voucher.voucherId;

			try {
				const result = await deleteVoucher({
					variables: {
						input: {
							voucherId: voucher.voucherId
						}
					},
					refetchQueries: ['Vouchers']
				});

				const checkResult = result.data?.deleteVoucher;

				if (checkResult?.success) {
					setLocalVouchers([]);
					showMutationSuccessToast('vouchers', 'delete');

					// Find the next voucher to select after deletion (before refresh)
					if (wasSelectedVoucher && vouchers) {
						const nextVoucherId = findNextVoucherToSelect(
							voucher.voucherId,
							vouchers
						);
						if (nextVoucherId) {
							setPendingSelectionId(nextVoucherId);
						} else {
							// No more vouchers, clear selection
							setSelectedVoucher(null);
						}
					}

					// Refresh the data table - useEffect will handle selection
					refresh();
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete voucher: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete voucher error:', error);
				toast.error('Failed to delete voucher. Please try again.');
			}
		}
	};

	const handleDeleteVoucherById = async (voucherId: string) => {
		const voucher = vouchers.find((v: VoucherListItem) => v.voucherId === voucherId);
		if (voucher) {
			await handleDeleteVoucher(voucher);
		}
	};

	const handleCloneVoucher = async (voucherId: string) => {
		try {
			// Fetch full voucher details for cloning
			const { data } = await apolloClient.query({
				query: VOUCHER_QUERY,
				variables: { voucherId },
				fetchPolicy: 'network-only'
			});

			const voucher = data?.voucher;
			if (!voucher) {
				toast.error(t('vouchers:messages.cloneError'));
				return;
			}

			// Use the cloneVoucher function for proper ID handling
			const clonedData = cloneVoucher({
				name: voucher.name,
				code: voucher.code,
				isAdHoc: voucher.isAdHoc,
				isCustomerSpecific: voucher.isCustomerSpecific,
				discountValue: voucher.discountValue,
				maxDiscountAmount: voucher.maxDiscountAmount,
				freeTimeMinutes: voucher.freeTimeMinutes,
				usageLimit: voucher.usageLimit,
				validUntilDate: voucher.validUntilDate,
				isActive: voucher.isActive,
				// Preserve foreign keys (lookups)
				voucherDiscountTypeEnum: voucher.voucherDiscountTypeEnum,
				campaignId: voucher.campaignId,
				customerId: voucher.customerId,
				facilityId: voucher.facilityId,
				rateId: voucher.rateId,
				rateBindingRequired: voucher.rateBindingRequired,
			});

			navigate('/vouchers/create', {
				state: {
					cloneData: {
						...clonedData,
						// Include display data for autocomplete labels
						campaign: voucher.campaign ? {
							campaignId: voucher.campaign.campaignId,
							name: voucher.campaign.name
						} : null,
						customer: voucher.customer ? {
							customerId: voucher.customer.customerId,
							firmName: voucher.customer.firmName,
							email: voucher.customer.email
						} : null,
						rate: voucher.rate ? {
							rateId: voucher.rate.rateId
						} : null
					}
				}
			});
		} catch (error) {
			console.error('Failed to clone voucher:', error);
			toast.error(t('vouchers:messages.cloneError'));
		}
	};

	const columns: ColDef<VoucherListItem>[] = [
		{
			headerName: t('list.columns.name'),
			field: 'name',
			flex: 1,
			valueGetter: params => params.data?.name ?? '—',
			minWidth: 150
		},
		{
			headerName: t('list.columns.code'),
			field: 'code',
			width: 100
		},
		{
			headerName: t('list.columns.type'),
			field: 'voucherDiscountType.name',
			valueGetter: params =>
				params.data?.voucherDiscountType?.name ?? '—',
			width: 150
		},
		{
			headerName: t('list.columns.usage'),
			field: 'usageLimit',
			valueGetter: params => {
				const { currentUsageCount, usageLimit } = params.data ?? {};
				if (usageLimit == null) {
					return `${currentUsageCount ?? 0} / ${t('placeholders.unlimited')}`;
				}
				return `${currentUsageCount ?? 0} / ${usageLimit}`;
			},
			width: 80
		},
		{
			headerName: t('list.columns.expires'),
			field: 'validUntilDate',
			valueGetter: params => formatDate(params.data?.validUntilDate),
			width: 100
		},
		{
			headerName: t('list.columns.status'),
			field: 'isActive',
			width: 120,
			minWidth: 120,
			cellRenderer: ActiveInactiveCellRenderer
		},
		...(showDeleteColumn ? [{
			headerName: '',
			field: undefined,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: DeleteCellRenderer,
			cellRendererParams: {
				onDelete: handleDeleteVoucher
			},
			sortable: false,
			filter: false,
			resizable: false
		}] : [])
	];

	return (
		<ListPageLayout
			gridId="vouchers-list"
			columns={columns}
			data={vouchers}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={isLoading || deleteLoading}
			onRowClick={item => {
				setSelectedVoucher(item);
			}}
			onCreate={goToCreate}
			onRefresh={refresh}
			selectedItem={selectedVoucher}
			detailsTitle={t('vouchers:view.title')}
			onEdit={id => goToEdit(id)}
			onDelete={handleDeleteVoucherById}
			onClone={handleCloneVoucher}
			onOpenInNewWindow={id => openEntityInNewWindow('vouchers', id)}
			renderDetailsContent={item => (
				<VoucherDetails
					voucherId={item.voucherId}
					activeTab={activeDetailsTab}
					onTabChange={setActiveDetailsTab}
					hideTabsList={true}
				/>
			)}
			renderDetailsHeader={(voucher, actions) => (
				<VoucherDetailsHeader voucher={voucher} {...actions} />
			)}
			getRecordId={item => item.voucherId}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
});

VouchersList.displayName = 'VouchersList';
